import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Api } from '../application/panelApi';
import { NodePicks } from '../components/NodePicks';
import { toast } from '../components/ToastHost';
import { clLiveStats, isLiveStatus, shortDateTime, type Cluster, type WarmupJob } from '../features/clusters';
import { useClusterLive } from '../hooks/useClusterLive';
import { errText, nf, plural } from '../lib/format';
import { useApp } from '../store/AppContext';

const LOCK_TITLE = 'Пока идёт запуск, этим управляет он';
const noop = () => undefined;

type FarmSettings = {
  farmInfinite?: boolean;
  farmTarget?: number;
  farmClusterIds?: string[];
};

type FarmPatch = {
  infinite?: boolean;
  target?: number;

  farmClusterIds?: string[];
};

export function WarmupPanel() {
  const {
    clusters: rawClusters,
    controlMode,
    settingsPayload,
    autopilotLocked: locked,
    ui,
    setUi,
    audit,
    refreshClusters,
    refreshSettings,
    refreshAutopilot,
    goto,
  } = useApp();
  const clusters = rawClusters as Cluster[];
  const control = controlMode === 'control';

  const [jobs, setJobs] = useState<WarmupJob[]>([]);
  const [pool, setPool] = useState(0);

  const [amount, setAmount] = useState('50');
  const [createProfiles, setCreateProfiles] = useState(true);
  const [force, setForce] = useState(false);
  const [starting, setStarting] = useState(false);


  const [farmOverride, setFarmOverride] = useState<FarmSettings | null>(null);
  const [targetDraft, setTargetDraft] = useState<string | null>(null);
  const saveSeq = useRef(0);



  const loadJobs = useCallback(async () => {
    if (!control) {
      setJobs([]);
      setPool(0);
      return;
    }
    try {
      const data = await Api.warmup.jobs();
      setJobs((data.jobs || []) as WarmupJob[]);
    } catch {
      setJobs([]);
    }
    try {
      setPool(Number((await Api.warmup.pool()).remaining) || 0);
    } catch {
      setPool(0);
    }
  }, [control]);

  const tick = useCallback(
    async () =>
      void (await Promise.all([
        loadJobs().catch(noop),
        refreshSettings().catch(noop),
        refreshAutopilot().catch(noop),
      ])),
    [loadJobs, refreshSettings, refreshAutopilot],
  );


  useClusterLive({ onTick: tick });


  useEffect(() => {
    let alive = true;
    void (async () => {
      await Promise.all([
        loadJobs().catch(noop),
        control ? refreshClusters().catch(noop) : undefined,
        refreshSettings().catch(noop),
        refreshAutopilot().catch(noop),
      ]);
      if (!alive || !control) return;
      try {
        await Api.clusters.pingAll();
        if (alive) await refreshClusters();
      } catch {

      }
    })();
    return () => {
      alive = false;
    };
  }, [control, loadJobs, refreshClusters, refreshSettings, refreshAutopilot]);



  const settings = useMemo<FarmSettings>(
    () => ({ ...((settingsPayload?.settings ?? {}) as FarmSettings), ...(farmOverride ?? {}) }),
    [settingsPayload, farmOverride],
  );
  const infinite = settings.farmInfinite !== false;
  const fTarget = Number(settings.farmTarget) || 0;
  const farmOff = !infinite && fTarget === 0;
  const farmIds = useMemo(
    () => (Array.isArray(settings.farmClusterIds) ? settings.farmClusterIds : []),
    [settings.farmClusterIds],
  );

  const stats = useMemo(() => {
    const live = clusters.filter((c) => c.enabled && isLiveStatus(c));
    const free = live.reduce((a, c) => a + Math.max(0, (c.profileLimit || 0) - clLiveStats(c).profilesActive), 0);
    const running = jobs.filter((j) => j.status === 'running').length;
    const jobsEnqueued = jobs
      .filter((j) => j.status === 'done')
      .reduce((a, j) => a + (j.jobsEnqueued || 0), 0);
    const liveActive = clusters.reduce((a, c) => a + clLiveStats(c).profilesActive, 0);
    const warming = clusters.reduce((a, c) => a + clLiveStats(c).profilesWarming, 0);
    const readyForTarget = clusters.reduce((a, c) => a + clLiveStats(c).profilesReadyForTarget, 0);
    return { live, free, running, jobsEnqueued, liveActive, warming, readyForTarget };
  }, [clusters, jobs]);

  const { live, free, running, jobsEnqueued, liveActive, warming, readyForTarget } = stats;

  const poolNote =
    control && pool > 0
      ? ` Ещё ${nf.format(pool)} профилей стоят в очереди — они подготовятся в любом случае (очистите очередь, если не нужно).`
      : '';

  let farmHint = '';
  if (locked) farmHint = 'Пока идёт запуск, подготовкой профилей управляет он сам.';
  else if (farmOff) farmHint = `Подготовка выключена. Уже начатые профили доготовятся (до ~40 мин).${poolNote}`;
  else if (!infinite && readyForTarget < fTarget) {
    farmHint = `Готовим до ${nf.format(fTarget)} профилей: готово ${nf.format(readyForTarget)}, потом остановимся.${poolNote}`;
  } else if (!infinite) {
    farmHint = `Готово ${nf.format(readyForTarget)} — это не меньше нужных ${nf.format(fTarget)}, подготовка остановлена.${poolNote}`;
  } else if (poolNote) farmHint = poolNote.trim();

  const enabledClusters = useMemo(() => clusters.filter((c) => c.enabled), [clusters]);



  const saveFarm = async (patch: FarmPatch) => {
    if (locked && patch.farmClusterIds === undefined) {
      toast('Пока идёт запуск, подготовкой управляет он', 'wa');
      return;
    }
    const nextInfinite = patch.infinite ?? infinite;
    const draft = targetDraft ?? (infinite ? String(Math.max(1, readyForTarget)) : String(fTarget));
    const target = patch.target ?? Math.max(0, Math.floor(+draft || 0));
    const farmClusterIds = patch.farmClusterIds ?? farmIds;
    const cur = (settingsPayload?.settings ?? {}) as Record<string, unknown>;
    const next = {
      ...cur,
      farmInfinite: nextInfinite,
      farmTarget: nextInfinite ? 0 : target,
      farmClusterIds,
    };
    const seq = ++saveSeq.current;
    setFarmOverride({ farmInfinite: next.farmInfinite, farmTarget: next.farmTarget, farmClusterIds });
    setTargetDraft(null);
    try {
      await Api.settings.save({ settings: next });
      if (patch.farmClusterIds !== undefined) {
        toast(farmClusterIds.length ? `Подготовка: ${farmClusterIds.length} нод` : 'Подготовка: все ноды', 'ok');
      } else {
        toast(
          nextInfinite
            ? 'Профили готовятся постоянно'
            : target === 0
              ? 'Подготовка выключена — начатые профили доготовятся'
              : `Готовим до ${nf.format(target)} профилей, затем стоп`,
          'ok',
        );
      }
      await refreshSettings().catch(noop);
    } catch (err) {
      toast(errText(err), 'err');
    } finally {

      if (seq === saveSeq.current) setFarmOverride(null);
    }
  };

  const commitTarget = () => {
    if (targetDraft === null) return;
    void saveFarm({ target: Math.max(0, Math.floor(+targetDraft || 0)) });
  };



  const clearPool = async () => {
    if (locked) {
      toast('Пока идёт запуск, очередью управляет он', 'wa');
      return;
    }
    try {
      setPool(Number((await Api.warmup.clearPool()).remaining) || 0);
      audit('Очистил пул прогрева', '');
      toast('Очередь подготовки очищена', 'ok');
    } catch (err) {
      toast(errText(err), 'err');
    }
  };

  const startIds = useMemo(() => (Array.isArray(ui.warmupStartIds) ? ui.warmupStartIds : []), [ui.warmupStartIds]);

  const start = async () => {
    if (locked) {
      toast('Пока идёт запуск, ручная подготовка недоступна', 'wa');
      return;
    }
    const count = Math.max(1, +amount || 1);
    setStarting(true);
    try {
      const data = await Api.warmup.start({ count, clusterIds: startIds, createProfiles, force }, control);
      if (control && data.pooled !== undefined) {
        setPool(Number(data.remaining) || 0);
        await loadJobs().catch(noop);
        audit('Добавил в пул прогрева', `${count} профилей`);
        toast(`В очередь добавлено ${data.pooled}, всего в очереди ${data.remaining}`, 'ok');
      } else if (control) {
        const started = (data.jobs || []) as WarmupJob[];
        setJobs((prev) => [...started, ...prev].slice(0, 50));
        await refreshClusters().catch(noop);
        audit('Запустил прогрев', `${count} профилей`);
        const failed = started.filter((j) => j.status === 'error');
        const okJobs = started.filter((j) => j.status === 'done');
        if (failed.length && !okJobs.length) {
          toast(failed[0].error || 'Подготовка не запустилась', 'err');
        } else if (failed.length) {
          toast(`Частично: ок ${okJobs.length}, ошибок ${failed.length}`, 'wa');
        } else {
          const enq = okJobs.reduce((a, j) => a + (j.jobsEnqueued || 0), 0);
          const created = okJobs.reduce((a, j) => a + (j.profilesCreated || 0), 0);
          if (!createProfiles && enq < count) {
            toast(
              `Взято ${enq} из ${count} — новые профили не создавались. Включите «Создать новые, если не хватает».`,
              'wa',
            );
          } else if (createProfiles && created === 0 && enq < count) {
            toast(`Создано 0, поставлено ${enq} из ${count}. Проверьте свободные места на нодах.`, 'wa');
          } else {
            toast(`Подготовка запущена: создано ${created}, готовится ${enq || count} на ${okJobs.length} нод`, 'ok');
          }
        }
      } else {
        toast(`Поставлено в очередь: ${data.jobsEnqueued || 0} задач`, 'ok');
      }
    } catch (err) {
      toast(errText(err), 'err');
      await loadJobs().catch(noop);
    } finally {
      setStarting(false);
    }
  };

  const farmMode: 'infinite' | 'target' | 'off' = infinite ? 'infinite' : farmOff ? 'off' : 'target';
  const pickFarmMode = (mode: 'infinite' | 'target' | 'off') => {
    if (mode === farmMode) return;
    if (mode === 'infinite') void saveFarm({ infinite: true });
    else if (mode === 'off') void saveFarm({ infinite: false, target: 0 });
    else void saveFarm({ infinite: false, target: Math.max(1, fTarget || readyForTarget) });
  };
  const targetShown = targetDraft ?? String(fTarget);

  return (
    <section className={`lp-more${locked ? ' locked' : ''}`} id="page-warmup">
      <div className="lp-more-heading">
        <span className="lp-more-title">Ручная подготовка профилей</span>
        {locked ? <span className="lp-more-hint">Пока идёт запуск, этим управляет он сам</span> : null}
      </div>

      <div className="lp-more-body">
        <dl className="lp-stats">
          <div>
            <dt>Готовятся сейчас</dt>
            <dd id="wmNow">{nf.format(warming)}</dd>
          </div>
          <div>
            <dt>Готовы к работе</dt>
            <dd id="wmDone" className="ok">
              {nf.format(readyForTarget)}
            </dd>
          </div>
          <div>
            <dt>Работает браузеров</dt>
            <dd id="wmJobs">{nf.format(liveActive || running || jobsEnqueued)}</dd>
          </div>
          <div>
            <dt>Свободных мест на нодах</dt>
            <dd id="wmFree">{nf.format(free)}</dd>
          </div>
        </dl>

        <div className="lp-row">
          <span className="lp-k">Готовить профили</span>
          <div className="lp-seg" role="radiogroup" aria-label="Режим подготовки профилей">
            {(
              [
                ['infinite', 'Постоянно'],
                ['target', 'До количества'],
                ['off', 'Не готовить'],
              ] as const
            ).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                role="radio"
                aria-checked={farmMode === mode}
                className={farmMode === mode ? 'on' : undefined}
                disabled={locked}
                title={locked ? LOCK_TITLE : ''}
                onClick={() => pickFarmMode(mode)}
              >
                {label}
              </button>
            ))}
          </div>
          {farmMode === 'target' ? (
            <label className="lp-inline">
              <input
                type="number"
                id="wmTarget"
                min={1}
                max={500000}
                value={targetShown}
                disabled={locked}
                onChange={(e) => setTargetDraft(e.target.value)}
                onBlur={commitTarget}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitTarget();
                }}
              />
              <span>готовых профилей</span>
            </label>
          ) : null}
        </div>
        {farmHint ? (
          <p className="lp-lead" id="wmFarmModeHint">
            {farmHint}
          </p>
        ) : null}
        <div className="lp-row">
          <span className="lp-k">На каких нодах</span>
          <NodePicks
            id="wmFarmNodes"
            clusters={enabledClusters}
            selectedIds={farmIds}
            onChange={(ids) => void saveFarm({ farmClusterIds: ids })}
          />
        </div>

        <div className="lp-split">
          <div className="lp-sub">
            <h3>Загрузка нод</h3>
            <div className="dist" id="wDist">
              {!control ? (
                <div className="hint">Нет данных</div>
              ) : !live.length ? (
                <div className="hint">Нет работающих нод</div>
              ) : (
                live.map((c) => {
                  const s = clLiveStats(c);
                  const slots = Math.max(0, (c.profileLimit || 0) - s.profilesActive);
                  const pct = (s.profilesActive / Math.max(1, c.profileLimit || 1)) * 100;
                  return (
                    <div className="dist-row" key={c.id}>
                      <span className="nm">{c.name}</span>
                      <span className="track">
                        <i style={{ width: `${pct.toFixed(1)}%` }} />
                      </span>
                      <span className="vv">свободно {slots}</span>
                    </div>
                  );
                })
              )}
            </div>
            {control && pool > 0 ? (
              <div className="lp-pool" id="wmPoolRow">
                <span>
                  В очереди на подготовку <b id="wmPoolVal">{nf.format(pool)}</b>{' '}
                  {plural(pool, 'профиль', 'профиля', 'профилей')}
                </span>
                <button
                  type="button"
                  className="btn btn-sm"
                  id="wmPoolClear"
                  disabled={locked}
                  title={locked ? LOCK_TITLE : ''}
                  onClick={() => void clearPool()}
                >
                  Очистить очередь
                </button>
              </div>
            ) : null}
          </div>

          <div className="lp-sub">
            <h3>Подготовить сейчас</h3>
            <label className="lp-inline" htmlFor="wmAmount">
              <span>Сколько профилей</span>
              <input
                type="number"
                id="wmAmount"
                min={1}
                max={50000}
                value={amount}
                disabled={locked}
                title={locked ? LOCK_TITLE : ''}
                onChange={(e) => setAmount(e.target.value)}
              />
            </label>
            <NodePicks
              id="wmStartNodes"
              clusters={enabledClusters}
              selectedIds={startIds}
              disabled={locked}
              onChange={(ids) => setUi({ warmupStartIds: ids })}
            />
            <div className="set-row">
              <div className="txt">
                <b>Создать новые, если не хватает</b>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  id="wmCreate"
                  checked={createProfiles}
                  disabled={locked}
                  onChange={(e) => setCreateProfiles(e.target.checked)}
                />
                <i />
              </label>
            </div>
            <div className="set-row">
              <div className="txt">
                <b>Готовить повторно в тот же день</b>
              </div>
              <label className="switch">
                <input
                  type="checkbox"
                  id="wmForce"
                  checked={force}
                  disabled={locked}
                  onChange={(e) => setForce(e.target.checked)}
                />
                <i />
              </label>
            </div>
            <button
              type="button"
              className="btn btn-primary lp-sub-go"
              id="wmStart"
              disabled={locked || starting}
              title={locked ? LOCK_TITLE : ''}
              onClick={() => void start()}
            >
              Подготовить
            </button>
          </div>
        </div>

        <details className="lp-history">
          <summary>
            История запусков <span className="lp-count">{jobs.length}</span>
          </summary>
          {jobs.length ? (
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Когда</th>
                    <th>Ноды</th>
                    <th>Профилей</th>
                    <th>Результат</th>
                  </tr>
                </thead>
                <tbody id="wBody">
                  {jobs.map((j, i) => (
                    <tr key={j.id ?? `${j.startedAt}-${i}`}>
                      <td data-l="Когда" className="mono">
                        {shortDateTime(j.startedAt)}
                      </td>
                      <td data-l="Ноды">{j.clusterName}</td>
                      <td data-l="Профилей">{j.status === 'running' ? '…' : j.profilesCreated || j.count || 0}</td>
                      <td data-l="Результат">
                        {j.error ? (
                          <span className="subnum" style={{ color: 'var(--err)' }}>
                            {j.error}
                          </span>
                        ) : (
                          <span className="subnum">{j.jobsEnqueued || 0} поставлено на ноду</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="lp-empty">Запусков пока не было.</div>
          )}
        </details>

        <button type="button" className="lp-link" onClick={() => goto('logs')}>
          Открыть логи нод
        </button>
      </div>
    </section>
  );
}
